/* Oche - canonical data + API schemas. All data sources normalise to these shapes. */
import { z } from 'zod';

export const SessionStatus = z.enum(['active', 'completed']);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const TrendSchema = z.enum(['up', 'down', 'flat']);

/** Stored R2 key or absolute URL accepted on write paths. */
export const MediaRefInput = z.union([
  z.string().url(),
  z.string().regex(/^(photos|videos)\/[a-f0-9]{64}\.[a-z0-9]+$/),
]);

export const PlayerSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(80),
  score: z.number().int(),
  photoUrl: z.string().url().nullable().optional(),
  position: z.number().int().nonnegative().default(0),
});
export type Player = z.infer<typeof PlayerSchema>;

export const ScoreEventSchema = z.object({
  id: z.string().uuid(),
  playerId: z.string().uuid(),
  delta: z.number().int(),
  newScore: z.number().int(),
  createdAt: z.string().datetime(),
});
export type ScoreEvent = z.infer<typeof ScoreEventSchema>;

export const SessionSchema = z.object({
  id: z.string().uuid(),
  status: SessionStatus,
  title: z.string().min(1).max(160),
  videoUrl: z.string().url().nullable().optional(),
  videoPoster: z.string().url().nullable().optional(),
  hlsUrl: z.string().url().nullable().optional(),
  players: z.array(PlayerSchema),
  scoreEvents: z.array(ScoreEventSchema).default([]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Session = z.infer<typeof SessionSchema>;

/* Lightweight row for the list view (no nested events). */
export const SessionSummarySchema = SessionSchema.pick({
  id: true,
  status: true,
  title: true,
  videoPoster: true,
  createdAt: true,
}).extend({ playerCount: z.number().int().nonnegative() });
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

/* ---------- Inputs ---------- */
export const CreateSessionInput = z
  .object({
    title: z.string().min(1).max(160),
    status: SessionStatus.default('active'),
    videoUrl: MediaRefInput.optional(),
    videoPoster: MediaRefInput.optional(),
    players: z
      .array(
        z.object({
          name: z.string().min(1).max(80),
          score: z.number().int().default(0),
          photoUrl: MediaRefInput.optional(),
          position: z.number().int().nonnegative().default(0),
        }),
      )
      .min(1)
      .max(12),
  })
  .strict();
export type CreateSessionInput = z.infer<typeof CreateSessionInput>;

export const ScoreChange = z
  .object({
    playerId: z.string().uuid(),
    delta: z.number().int().optional(),
    set: z.number().int().optional(),
  })
  .strict()
  .refine((v) => v.delta !== undefined || v.set !== undefined, {
    message: 'Provide either `delta` or `set`.',
  });

export const PlayerPhotoUpdate = z
  .object({
    playerId: z.string().uuid(),
    photoUrl: MediaRefInput,
  })
  .strict();

export const PatchSessionInput = z
  .object({
    status: SessionStatus.optional(),
    scores: z.array(ScoreChange).max(12).optional(),
    videoUrl: MediaRefInput.optional(),
    videoPoster: MediaRefInput.optional(),
    hlsUrl: MediaRefInput.optional(),
    playerPhotos: z.array(PlayerPhotoUpdate).max(12).optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.status !== undefined ||
      (v.scores && v.scores.length > 0) ||
      v.videoUrl !== undefined ||
      v.videoPoster !== undefined ||
      v.hlsUrl !== undefined ||
      (v.playerPhotos && v.playerPhotos.length > 0),
    { message: 'Nothing to update.' },
  );
export type PatchSessionInput = z.infer<typeof PatchSessionInput>;

/* ---------- Response envelopes ---------- */
export const ApiError = z.object({
  error: z.string(),
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ApiError = z.infer<typeof ApiError>;

export const Paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ data: z.array(item), nextCursor: z.string().nullable() });
